/**
 * KAPE Faculty Preview — Learner Course Player
 * Route: `/kape/preview?token=…`  (public, signed-token gated)
 *
 * Renders the entire course as the student would experience it:
 *   · Course outline sidebar (Modules → Lessons) with current + completed markers
 *   · Header with course title, current module, current lesson, "Lesson X of Y",
 *     and an overall course progress bar
 *   · Slide viewer that deterministically builds a slide sequence from the
 *     lesson's existing saved fields (no AI, no invented content)
 *   · Previous / Next slide and Previous / Next lesson controls, keyboard arrows
 *   · Mark Lesson Complete (local preview state only — never writes to student
 *     progress in the database)
 *   · Native audio player when narration audio exists; friendly message otherwise
 *   · Interactive knowledge-check questions (submit → reveal correct + rationale)
 *   · Faculty Preview banner + safety notice
 *   · Mobile: outline collapses into a drawer
 *
 * Uses the extended /api/kape/preview/verify response which now returns
 *   { course, modules, lessons, lesson, sibling_lessons, token_expires_at }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSm,
  Check, CircleDot, List, X, Menu, ArrowLeft, ArrowRight,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "kape-preview-progress";

// ─────────────────────────────────────────────────────────────
// Slide builder — deterministic, no AI, no invented content
// ─────────────────────────────────────────────────────────────
function buildSlides(lesson) {
  if (!lesson) return [];
  const slides = [];

  // 1. Objectives slide (only if objectives present)
  if ((lesson.objectives || []).length > 0) {
    slides.push({
      kind: "objectives",
      title: "Learning Objectives",
      body: { objectives: lesson.objectives },
    });
  }

  // 2. Instructional content — split by H2 headings when present.
  //    Keep the whole block otherwise. Skip when empty.
  const md = (lesson.content_md || "").trim();
  if (md) {
    // Split on lines beginning with "## ". Preserve the heading with
    // its section body.
    const parts = md.split(/\n(?=##\s)/g).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      parts.forEach((section, idx) => {
        const headMatch = section.match(/^##\s+(.+)$/m);
        const heading = headMatch ? headMatch[1].trim() : `Section ${idx + 1}`;
        const body = section.replace(/^##\s+.+\n?/, "").trim();
        slides.push({
          kind: "content", title: heading, body: { markdown: body },
        });
      });
    } else {
      slides.push({
        kind: "content", title: "Lesson Content",
        body: { markdown: md },
      });
    }
  }

  // 3. Key terms
  if ((lesson.key_terms || []).length > 0) {
    slides.push({
      kind: "key_terms", title: "Key Terms",
      body: { terms: lesson.key_terms },
    });
  }

  // 4. Example / case
  if ((lesson.examples || "").trim()) {
    slides.push({
      kind: "example", title: "Example",
      body: { markdown: lesson.examples },
    });
  }

  // 5. Activity
  if ((lesson.activities || "").trim()) {
    slides.push({
      kind: "activity", title: "Learning Activity",
      body: { markdown: lesson.activities },
    });
  }

  // 6. Reflection
  if ((lesson.reflection_prompt || "").trim()) {
    slides.push({
      kind: "reflection", title: "Reflection Prompt",
      body: { markdown: lesson.reflection_prompt },
    });
  }

  // 7. Knowledge check (interactive)
  const kc = lesson.kape?.knowledge_check || [];
  if (kc.length > 0) {
    slides.push({
      kind: "knowledge_check", title: "Knowledge Check",
      body: { questions: kc },
    });
  }

  // 8. Takeaways + references summary
  const takeaways = lesson.student_takeaways || [];
  const refs = lesson.references || [];
  if (takeaways.length > 0 || refs.length > 0) {
    slides.push({
      kind: "summary", title: "Key Takeaways",
      body: { takeaways, references: refs },
    });
  }

  // 9. Lesson complete slide (always present so learners have a
  //    dedicated completion step)
  slides.push({
    kind: "complete", title: "Lesson Complete",
    body: {},
  });

  // 10. Merge server-persisted slide media (image + per-slide audio)
  //     onto the client-built sequence by ordinal position. The server
  //     builder produces the SAME ordering, so index match is safe. We
  //     never let a placeholder or unconfigured image_url through — the
  //     student LMS should only display real, resolvable media.
  const serverSlides = lesson.kape?.slides || [];
  slides.forEach((s, idx) => {
    const meta = serverSlides[idx];
    if (!meta) return;
    const imgOk = meta.image_url
      && meta.image_source
      && !["placeholder", "pending", ""].includes(meta.image_source);
    if (imgOk) {
      s.image_url = meta.image_url;
      s.image_alt = meta.image_alt || meta.title || s.title;
      s.image_caption = meta.image_caption || "";
    }
    if (meta.audio_url && meta.audio_status === "generated") {
      s.audio_url = meta.audio_url;
      s.audio_duration = meta.audio_duration_seconds || 0;
    }
    s._slide_id = meta.id;
  });

  return slides;
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function KapePreview() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [activeLessonId, setActiveLessonId] = useState("");
  const [slideIdx, setSlideIdx] = useState(0);
  const [outlineOpen, setOutlineOpen] = useState(false);   // mobile drawer
  const [completed, setCompleted] = useState({});           // {lessonId: true}
  const contentRef = useRef(null);

  // Load full course via signed token — no auth needed.
  const load = useCallback(async () => {
    if (!token) { setErr("Missing preview token."); return; }
    setErr("");
    try {
      const url = new URL(`${BACKEND}/api/kape/preview/verify`);
      url.searchParams.set("token", token);
      const r = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErr(body.detail || `Preview unavailable (${r.status})`);
        return;
      }
      const body = await r.json();
      setData(body);
      // Restore last position from localStorage (per-course, per-token-course).
      const saved = readSavedProgress(body.course?.id);
      const firstLessonId = saved.lessonId
        || body.lesson?.id
        || body.lessons?.[0]?.id
        || "";
      setActiveLessonId(firstLessonId);
      setSlideIdx(saved.slideIdx || 0);
      setCompleted(saved.completed || {});
    } catch (e) {
      setErr("Preview could not load. Refresh or ask for a new preview token.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const course = data?.course;
  const modules = data?.modules || [];
  const lessons = data?.lessons || [];
  const lessonById = useMemo(() => {
    const m = new Map();
    lessons.forEach((l) => m.set(l.id, l));
    return m;
  }, [lessons]);

  // Course-wide ordered lesson list, sorted per module order then lesson order.
  const orderedLessons = useMemo(() => {
    const out = [];
    const sortedModules = [...modules].sort(
      (a, b) => (a.order || 0) - (b.order || 0));
    sortedModules.forEach((m) => {
      (m.lesson_ids || []).forEach((lid) => {
        const l = lessonById.get(lid);
        if (l) out.push(l);
      });
    });
    // Fallback: any lessons not attached to a module
    lessons.forEach((l) => {
      if (!out.find((x) => x.id === l.id)) out.push(l);
    });
    return out;
  }, [modules, lessons, lessonById]);

  const currentLesson = lessonById.get(activeLessonId) || orderedLessons[0];
  const currentLessonIndex = orderedLessons.findIndex(
    (l) => l.id === activeLessonId);
  const currentModule = useMemo(() => {
    if (!currentLesson) return null;
    return modules.find((m) => m.id === currentLesson.module_id) || null;
  }, [modules, currentLesson]);

  const slides = useMemo(() => buildSlides(currentLesson), [currentLesson]);
  const safeSlideIdx = Math.min(slideIdx, Math.max(0, slides.length - 1));
  const currentSlide = slides[safeSlideIdx];

  // Persist progress locally (never touches the backend — faculty preview
  // must not write to real student progress).
  useEffect(() => {
    if (!course?.id || !activeLessonId) return;
    writeSavedProgress(course.id, {
      lessonId: activeLessonId, slideIdx: safeSlideIdx, completed,
    });
  }, [course?.id, activeLessonId, safeSlideIdx, completed]);

  // Reset slide index when lesson changes (scroll to top).
  const switchLesson = useCallback((lid) => {
    setActiveLessonId(lid);
    setSlideIdx(0);
    setOutlineOpen(false);
    contentRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
  }, []);

  const gotoPrevSlide = useCallback(() => {
    if (safeSlideIdx > 0) {
      setSlideIdx(safeSlideIdx - 1);
      contentRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
    } else {
      // Wrap to previous lesson's last slide
      if (currentLessonIndex > 0) {
        const prev = orderedLessons[currentLessonIndex - 1];
        const prevSlides = buildSlides(prev);
        setActiveLessonId(prev.id);
        setSlideIdx(Math.max(0, prevSlides.length - 1));
        contentRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
      }
    }
  }, [safeSlideIdx, currentLessonIndex, orderedLessons]);

  const gotoNextSlide = useCallback(() => {
    if (safeSlideIdx < slides.length - 1) {
      setSlideIdx(safeSlideIdx + 1);
      contentRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
    } else if (currentLessonIndex < orderedLessons.length - 1) {
      // On last slide → advance to next lesson
      const nxt = orderedLessons[currentLessonIndex + 1];
      setActiveLessonId(nxt.id);
      setSlideIdx(0);
      contentRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
    }
  }, [safeSlideIdx, slides.length, currentLessonIndex, orderedLessons]);

  const gotoPrevLesson = useCallback(() => {
    if (currentLessonIndex > 0) {
      switchLesson(orderedLessons[currentLessonIndex - 1].id);
    }
  }, [currentLessonIndex, orderedLessons, switchLesson]);

  const gotoNextLesson = useCallback(() => {
    if (currentLessonIndex < orderedLessons.length - 1) {
      switchLesson(orderedLessons[currentLessonIndex + 1].id);
    }
  }, [currentLessonIndex, orderedLessons, switchLesson]);

  const markComplete = useCallback(() => {
    if (!activeLessonId) return;
    setCompleted((c) => ({ ...c, [activeLessonId]: true }));
  }, [activeLessonId]);

  // Keyboard navigation
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;
      if (e.key === "ArrowRight") { e.preventDefault(); gotoNextSlide(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); gotoPrevSlide(); }
      else if (e.key === "Escape") { setOutlineOpen(false); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [gotoNextSlide, gotoPrevSlide]);

  // Overall progress
  const completedCount = Object.values(completed).filter(Boolean).length;
  const totalLessons = orderedLessons.length;
  const progressPct = totalLessons
    ? Math.round((completedCount / totalLessons) * 100)
    : 0;

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8"
           data-testid="kape-preview-error">
        <div className="max-w-md text-center">
          <div className="text-lg font-semibold text-slate-800">Preview unavailable</div>
          <div className="mt-2 text-sm text-slate-600">{err}</div>
          <div className="mt-6 text-xs text-slate-500">
            Ask an instructor to open Preview again — tokens expire after 30 minutes.
          </div>
        </div>
      </div>
    );
  }

  if (!data || !currentLesson) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500"
           data-testid="kape-preview-loading">
        Loading preview…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col"
         data-testid="kape-preview-shell">
      {/* Faculty preview banner */}
      <div className="bg-amber-100 border-b border-amber-200 px-4 py-2 text-xs
                      text-amber-900 flex items-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={() => setOutlineOpen((v) => !v)}
          className="md:hidden inline-flex items-center gap-1 rounded px-2 py-1
                     hover:bg-amber-200"
          data-testid="kape-preview-outline-toggle"
        >
          <Menu className="w-4 h-4" />
          Outline
        </button>
        <span className="font-semibold uppercase tracking-widest">
          Faculty Preview
        </span>
        <span className="hidden sm:inline">
          You are viewing this course exactly as a student would. Your activity
          here does not affect any student record.
        </span>
        <button
          type="button"
          onClick={() => {
            if (!course?.id) return;
            writeSavedProgress(course.id, {
              lessonId: orderedLessons[0]?.id, slideIdx: 0, completed: {},
            });
            setCompleted({});
            setActiveLessonId(orderedLessons[0]?.id || "");
            setSlideIdx(0);
          }}
          className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ background: "#FFFFFF", color: "#78350F",
                   border: "1px solid #D97706" }}
          data-testid="kape-preview-reset-progress"
        >
          Reset Preview Progress
        </button>
        <span className="opacity-70">
          Token expires {data.token_expires_at
            ? new Date(data.token_expires_at * 1000).toLocaleTimeString()
            : "—"}
        </span>
      </div>

      {/* Progress bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex-shrink-0"
           data-testid="kape-preview-progress-bar">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-purple-700 font-semibold">
              {course?.title || "Course preview"}
            </div>
            <div className="mt-0.5 text-sm text-slate-800 truncate"
                 data-testid="kape-preview-current-lesson-title">
              {currentModule ? (
                <>
                  <span className="text-slate-500">{currentModule.title} · </span>
                  <span className="font-semibold">{currentLesson.title}</span>
                </>
              ) : (
                <span className="font-semibold">{currentLesson.title}</span>
              )}
            </div>
          </div>
          <div className="hidden sm:block text-xs text-slate-600 whitespace-nowrap"
               data-testid="kape-preview-lesson-counter">
            Lesson {currentLessonIndex + 1} of {totalLessons}
          </div>
          <div className="flex-shrink-0 w-32">
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-purple-700 transition-all"
                   style={{ width: `${progressPct}%` }} />
            </div>
            <div className="mt-1 text-[10px] text-slate-500 text-right"
                 data-testid="kape-preview-progress-pct">
              {progressPct}% complete
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar — outline (desktop persistent, mobile drawer) */}
        <OutlineSidebar
          modules={modules}
          lessonById={lessonById}
          activeLessonId={activeLessonId}
          completed={completed}
          onPick={switchLesson}
          open={outlineOpen}
          onClose={() => setOutlineOpen(false)}
        />

        {/* Main content area */}
        <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <div ref={contentRef}
               className="flex-1 overflow-y-auto px-4 sm:px-6 py-6"
               data-testid="kape-preview-content">
            <div className="max-w-3xl mx-auto space-y-4">
              {/* Slide counter */}
              <div className="flex items-center justify-between text-xs text-slate-500">
                <button
                  type="button"
                  onClick={() => setOutlineOpen(true)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1
                             hover:bg-slate-100 text-slate-700"
                  data-testid="kape-preview-back-to-outline"
                >
                  <List className="w-3.5 h-3.5" />
                  Course outline
                </button>
                <span data-testid="kape-preview-slide-counter">
                  Slide {slides.length ? safeSlideIdx + 1 : 0} of {slides.length}
                </span>
              </div>

              {/* Slide */}
              <SlideRenderer
                slide={currentSlide}
                lesson={currentLesson}
                isCompleted={!!completed[currentLesson.id]}
                onMarkComplete={markComplete}
                onNextLesson={gotoNextLesson}
                hasNextLesson={currentLessonIndex < orderedLessons.length - 1}
                onBackToOutline={() => setOutlineOpen(true)}
                onGotoNextIncomplete={() => {
                  const nxt = orderedLessons.find(
                    (l) => !completed[l.id] && l.id !== currentLesson.id);
                  if (nxt) switchLesson(nxt.id);
                }}
                isLastLessonButEarlierIncomplete={
                  currentLessonIndex === orderedLessons.length - 1
                  && orderedLessons.slice(0, -1)
                       .some((l) => !completed[l.id])}
                allComplete={completedCount === totalLessons}
              />

              {/* Narration player */}
              <NarrationPanel
                narration={currentLesson.kape?.narration || {}}
                lessonId={currentLesson.id}
              />
            </div>
          </div>

          {/* Sticky nav footer */}
          <div className="border-t border-slate-200 bg-white px-4 py-3
                          flex items-center gap-2 flex-shrink-0"
               data-testid="kape-preview-nav-footer">
            <button
              type="button"
              onClick={gotoPrevSlide}
              disabled={safeSlideIdx === 0 && currentLessonIndex === 0}
              className="inline-flex items-center gap-1 rounded-full px-3 py-2
                         text-xs font-semibold border border-slate-300
                         text-slate-700 disabled:opacity-40 hover:bg-slate-50"
              data-testid="kape-preview-prev-slide"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              type="button"
              onClick={gotoPrevLesson}
              disabled={currentLessonIndex === 0}
              className="hidden sm:inline-flex items-center gap-1 rounded-full
                         px-3 py-2 text-xs font-semibold border border-slate-300
                         text-slate-700 disabled:opacity-40 hover:bg-slate-50"
              data-testid="kape-preview-prev-lesson"
            >
              <ArrowLeft className="w-4 h-4" />
              Prev lesson
            </button>
            <div className="flex-1 text-center text-xs text-slate-500">
              {currentSlide?.title || ""}
            </div>
            <button
              type="button"
              onClick={gotoNextLesson}
              disabled={currentLessonIndex >= orderedLessons.length - 1}
              className="hidden sm:inline-flex items-center gap-1 rounded-full
                         px-3 py-2 text-xs font-semibold border border-slate-300
                         text-slate-700 disabled:opacity-40 hover:bg-slate-50"
              data-testid="kape-preview-next-lesson"
            >
              Next lesson
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={gotoNextSlide}
              disabled={
                safeSlideIdx >= slides.length - 1 &&
                currentLessonIndex >= orderedLessons.length - 1
              }
              className="inline-flex items-center gap-1 rounded-full px-3 py-2
                         text-xs font-semibold bg-purple-900 text-white
                         disabled:opacity-40 hover:bg-purple-800"
              data-testid="kape-preview-next-slide"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Outline sidebar
// ─────────────────────────────────────────────────────────────
function OutlineSidebar({ modules, lessonById, activeLessonId, completed,
                          onPick, open, onClose }) {
  // Pre-expand the module containing the current lesson.
  const activeModuleId = useMemo(() => {
    const l = lessonById.get(activeLessonId);
    return l?.module_id || (modules[0]?.id ?? "");
  }, [activeLessonId, lessonById, modules]);
  const [expanded, setExpanded] = useState(() => new Set([activeModuleId]));

  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(activeModuleId);
      return next;
    });
  }, [activeModuleId]);

  const toggle = (mid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  };

  const body = (
    <nav
      className="w-full h-full bg-white border-r border-slate-200 overflow-y-auto"
      data-testid="kape-preview-outline"
      aria-label="Course outline"
    >
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <List className="w-4 h-4 text-purple-800" />
        <div className="text-sm font-semibold text-slate-800">Course Outline</div>
        <button
          type="button"
          onClick={onClose}
          className="md:hidden ml-auto p-1 rounded hover:bg-slate-100"
          aria-label="Close outline"
          data-testid="kape-preview-outline-close"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>
      </div>
      <ol className="py-1">
        {[...modules].sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((m, mi) => {
            const isOpen = expanded.has(m.id);
            const moduleLessons = (m.lesson_ids || [])
              .map((lid) => lessonById.get(lid))
              .filter(Boolean);
            return (
              <li key={m.id} data-testid={`kape-preview-outline-module-${mi + 1}`}>
                <button
                  type="button"
                  onClick={() => toggle(m.id)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left
                             hover:bg-slate-50"
                >
                  {isOpen
                    ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    : <ChevronRightSm className="w-4 h-4 text-slate-500 flex-shrink-0" />}
                  <span className="text-xs font-bold uppercase tracking-wider
                                    text-purple-800">
                    Module {mi + 1}
                  </span>
                  <span className="text-sm text-slate-800 truncate">
                    {(m.title || "").replace(/^Module\s+\d+:\s*/, "")}
                  </span>
                </button>
                {isOpen && (
                  <ul className="pb-1">
                    {moduleLessons.map((l, li) => {
                      const isCurrent = l.id === activeLessonId;
                      const isDone = !!completed[l.id];
                      return (
                        <li key={l.id}>
                          <button
                            type="button"
                            onClick={() => onPick(l.id)}
                            className={`w-full flex items-start gap-2 pl-11 pr-3
                                         py-1.5 text-left text-sm hover:bg-slate-50
                                         ${isCurrent
                                           ? "bg-purple-50 text-purple-900 font-semibold"
                                           : "text-slate-700"}`}
                            data-testid={`kape-preview-outline-lesson-${l.id}`}
                          >
                            <span className="mt-0.5 flex-shrink-0">
                              {isDone
                                ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                                : isCurrent
                                  ? <CircleDot className="w-3.5 h-3.5 text-purple-700" />
                                  : <span className="inline-block w-3.5 h-3.5
                                                     rounded-full border border-slate-300" />}
                            </span>
                            <span className="truncate">
                              <span className="text-slate-400 mr-1">
                                {mi + 1}.{li + 1}
                              </span>
                              {l.title}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
      </ol>
    </nav>
  );

  return (
    <>
      {/* Desktop: persistent sidebar */}
      <aside className="hidden md:block w-72 flex-shrink-0 h-full">
        {body}
      </aside>
      {/* Mobile: overlay drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex"
             role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-slate-900/40"
            onClick={onClose}
            data-testid="kape-preview-outline-overlay"
          />
          <div className="relative w-72 max-w-[85vw] h-full bg-white">
            {body}
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Slide renderer — one per slide.kind
// ─────────────────────────────────────────────────────────────
function SlideRenderer({ slide, lesson, isCompleted, onMarkComplete,
                          onNextLesson, hasNextLesson, onBackToOutline,
                          onGotoNextIncomplete, isLastLessonButEarlierIncomplete,
                          allComplete }) {
  if (!slide) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-6 text-sm
                      text-slate-600" data-testid="kape-preview-slide-empty">
        This lesson has no rendered slides yet — no content has been generated
        for it.
      </div>
    );
  }

  const wrap = (children, extraCls = "") => (
    <section className={`rounded-2xl bg-white border border-slate-200 p-6
                          shadow-sm ${extraCls}`}
             data-testid={`kape-preview-slide-${slide.kind}`}>
      <div className="text-[11px] uppercase tracking-widest text-purple-700
                      font-semibold mb-2">
        {slide.title}
      </div>
      {slide.image_url ? (
        <figure className="mb-4">
          <img
            src={`${BACKEND}${slide.image_url}`}
            alt={slide.image_alt || slide.title}
            className="rounded-xl border border-slate-200 w-full max-h-72
                       object-cover"
            data-testid={`kape-preview-slide-image-${slide._slide_id || slide.kind}`}
          />
          {slide.image_caption && (
            <figcaption className="mt-1 text-xs text-slate-500">
              {slide.image_caption}
            </figcaption>
          )}
        </figure>
      ) : null}
      {children}
      {slide.audio_url ? (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3"
             data-testid={`kape-preview-slide-audio-panel-${slide._slide_id || slide.kind}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500
                          font-semibold mb-1">
            Slide narration
          </div>
          <audio
            src={`${BACKEND}${slide.audio_url}`}
            controls
            preload="none"
            className="w-full"
            data-testid={`kape-preview-slide-audio-${slide._slide_id || slide.kind}`}
          />
        </div>
      ) : null}
    </section>
  );

  if (slide.kind === "objectives") {
    return wrap(
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-4"
            data-testid="kape-preview-lesson-title">
          {lesson.title}
        </h1>
        <ul className="space-y-2 text-sm text-slate-800">
          {slide.body.objectives.map((o, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-purple-800 mt-1">✓</span>
              <span>{o}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (slide.kind === "content" || slide.kind === "example"
      || slide.kind === "activity" || slide.kind === "reflection") {
    return wrap(
      <div className="prose prose-sm max-w-none prose-headings:text-slate-900
                      prose-p:text-slate-800 prose-strong:text-slate-900
                      prose-li:text-slate-800 whitespace-pre-wrap text-sm
                      text-slate-800 leading-relaxed">
        {slide.body.markdown}
      </div>
    );
  }

  if (slide.kind === "key_terms") {
    return wrap(
      <dl className="space-y-3">
        {slide.body.terms.map((t, i) => (
          <div key={i}>
            {typeof t === "string" ? (
              <dt className="text-sm font-semibold text-slate-900">{t}</dt>
            ) : (
              <>
                <dt className="text-sm font-semibold text-slate-900">
                  {t.term || t.name || ""}
                </dt>
                <dd className="text-sm text-slate-700 mt-0.5">
                  {t.definition || t.description || ""}
                </dd>
              </>
            )}
          </div>
        ))}
      </dl>
    );
  }

  if (slide.kind === "knowledge_check") {
    return wrap(
      <KnowledgeCheckSlide questions={slide.body.questions} />
    );
  }

  if (slide.kind === "summary") {
    const { takeaways = [], references = [] } = slide.body;
    return wrap(
      <div className="space-y-4">
        {takeaways.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-slate-900 mb-2">
              Student Takeaways
            </div>
            <ul className="space-y-1.5 text-sm text-slate-800 list-disc pl-5">
              {takeaways.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
        )}
        {references.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-slate-900 mb-2">
              References
            </div>
            <ul className="space-y-1 text-xs text-slate-600 list-disc pl-5">
              {references.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (slide.kind === "complete") {
    const nextIncomplete = onGotoNextIncomplete && !allComplete;
    return wrap(
      <div className="text-center py-4">
        <div className="text-3xl mb-2">🎓</div>
        <div className="text-lg font-semibold text-slate-900">
          You&rsquo;ve reached the end of {lesson.title || "this lesson"}.
        </div>
        {isLastLessonButEarlierIncomplete && (
          <div className="mt-2 rounded-lg px-3 py-2 text-xs inline-block"
               style={{ background: "#FEF3C7", color: "#78350F",
                        border: "1px solid #FDE68A" }}
               data-testid="kape-preview-earlier-lessons-warning">
            You have completed this lesson, but earlier lessons remain.
          </div>
        )}
        <div className="mt-1 text-sm text-slate-600">
          Mark it complete to continue to the next lesson.
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onMarkComplete}
            disabled={isCompleted}
            className="rounded-full px-4 py-2 text-sm font-semibold"
            style={{ background: isCompleted ? "#94A3B8" : "#059669",
                     color: "#FFFFFF",
                     cursor: isCompleted ? "not-allowed" : "pointer",
                     opacity: isCompleted ? 0.85 : 1 }}
            data-testid="kape-preview-mark-complete"
          >
            {isCompleted ? <><Check className="inline w-4 h-4 mr-1" />Lesson complete</>
                          : "Mark Lesson Complete"}
          </button>
          {hasNextLesson && (
            <button
              type="button"
              onClick={onNextLesson}
              className="rounded-full px-4 py-2 text-sm font-semibold"
              style={{ background: "#FFFFFF", color: "#0F172A",
                       border: "1px solid #CBD5E1" }}
              data-testid="kape-preview-goto-next-lesson"
            >
              Continue to Next Lesson →
            </button>
          )}
          {nextIncomplete && (
            <button
              type="button"
              onClick={onGotoNextIncomplete}
              className="rounded-full px-4 py-2 text-sm font-semibold"
              style={{ background: "#F1F5F9", color: "#0F172A",
                       border: "1px solid #CBD5E1" }}
              data-testid="kape-preview-goto-next-incomplete"
            >
              Go to Next Incomplete Lesson
            </button>
          )}
          {onBackToOutline && (
            <button
              type="button"
              onClick={onBackToOutline}
              className="rounded-full px-4 py-2 text-sm font-semibold"
              style={{ background: "#FFFFFF", color: "#0F172A",
                       border: "1px solid #CBD5E1" }}
              data-testid="kape-preview-goto-outline"
            >
              Go to Course Outline
            </button>
          )}
        </div>
      </div>,
      "text-center"
    );
  }

  return wrap(
    <div className="text-sm text-slate-600">
      This section has no rendered content.
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Knowledge-check — interactive (submit → reveal correct + rationale)
// ─────────────────────────────────────────────────────────────
function KnowledgeCheckSlide({ questions }) {
  return (
    <div className="space-y-6">
      {questions.map((q, qi) => (
        <KnowledgeCheckQuestion key={qi} q={q} index={qi} />
      ))}
    </div>
  );
}

function KnowledgeCheckQuestion({ q, index }) {
  const [picked, setPicked] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const options = q.options || [];
  const correctIdx = options.findIndex((o) => o.correct || o.is_correct);
  return (
    <div className="border border-slate-200 rounded-xl p-4"
         data-testid={`kape-preview-kc-q-${index + 1}`}>
      <div className="text-sm font-semibold text-slate-900 mb-3">
        Question {index + 1}. {q.prompt || q.question || ""}
      </div>
      <ul className="space-y-2">
        {options.map((o, oi) => {
          const label = o.label || o.text || "";
          const isPicked = picked === oi;
          const isCorrect = submitted && oi === correctIdx;
          const isWrong = submitted && isPicked && oi !== correctIdx;
          return (
            <li key={oi}>
              <button
                type="button"
                disabled={submitted}
                onClick={() => setPicked(oi)}
                className={`w-full text-left rounded-lg border px-3 py-2
                             text-sm transition-colors
                             ${isCorrect ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                               : isWrong ? "border-red-400 bg-red-50 text-red-900"
                               : isPicked ? "border-purple-500 bg-purple-50 text-purple-900"
                               : "border-slate-200 text-slate-800 hover:bg-slate-50"}`}
                data-testid={`kape-preview-kc-q-${index + 1}-opt-${oi + 1}`}
              >
                {label}
              </button>
            </li>
          );
        })}
      </ul>
      {!submitted && (
        <button
          type="button"
          disabled={picked === null}
          onClick={() => setSubmitted(true)}
          className="mt-3 rounded-full px-3 py-1.5 text-xs font-semibold
                     bg-purple-900 text-white disabled:opacity-40 hover:bg-purple-800"
          data-testid={`kape-preview-kc-q-${index + 1}-submit`}
        >
          Submit answer
        </button>
      )}
      {submitted && (q.rationale || q.explanation) && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3
                        text-sm text-slate-800"
             data-testid={`kape-preview-kc-q-${index + 1}-explanation`}>
          <span className="font-semibold text-slate-900">Explanation: </span>
          {q.rationale || q.explanation}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Narration audio panel
// ─────────────────────────────────────────────────────────────
function NarrationPanel({ narration }) {
  const src = narration?.audio_url
    ? `${BACKEND}${narration.audio_url}`
    : "";
  if (!src) {
    return (
      <section className="rounded-2xl bg-slate-100 border border-dashed
                          border-slate-300 p-4 text-sm text-slate-600"
               data-testid="kape-preview-narration-missing">
        Narration has not been generated for this lesson.
      </section>
    );
  }
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-4"
             data-testid="kape-preview-narration-player">
      <div className="text-[11px] uppercase tracking-widest text-purple-700
                      font-semibold mb-2">Narration</div>
      <audio
        src={src}
        controls
        preload="none"
        className="w-full"
        data-testid="kape-preview-narration-audio"
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Local-storage progress (per-course). Faculty preview must NEVER
// write to a real student progress row on the backend.
// ─────────────────────────────────────────────────────────────
function readSavedProgress(courseId) {
  if (!courseId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(`${STORAGE_KEY}:${courseId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeSavedProgress(courseId, val) {
  if (!courseId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${STORAGE_KEY}:${courseId}`, JSON.stringify(val));
  } catch { /* quota / disabled — silently ignore */ }
}
